import { adminOnlyFeatureRoles } from '@/app/constants';
import store from '@/app/store';
import { AuthGuard } from '@/modules/auth/router/auth.guard';
import { OrganisationGuard } from '@/modules/auth/router/organisation.guard';
import { NavigationGuard, Route } from 'vue-router';
import multiguard from 'vue-router-multiguard';

type Next = Parameters<NavigationGuard>[2];

/**
 * The analytics pipeline designer is an admin-only tool, see `adminOnlyFeatures`. Non-admins get
 * the same 404 that `AuthGuard` returns for a disabled feature flag, rather than an access error,
 * so that the section is indistinguishable from one that is switched off.
 */
const AdminOnlyGuard = (to: Route, from: Route, next: Next) => {
    const roles: string[] = store.state.auth.user?.roles || [];
    if (adminOnlyFeatureRoles.some((role: string) => roles.includes(role))) return next();
    return next({ name: '404' });
};

export default [
    {
        name: 'workflows',
        path: '/analytics-pipelines',
        component: () => import(/* webpackChunkName: "workflows" */ '@/modules/workflow-designer/views/Workflows.vue'),
        meta: { title: 'Analytics Pipeline', feature: 'analytics' },
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },
    {
        name: 'workflow-designer:edit',
        path: '/analytics-pipelines/:id/designer',
        component: () =>
            import(/* webpackChunkName: "workflows" */ '@/modules/workflow-designer/views/WorkflowDesigner.vue'),
        props: true,
        meta: { title: 'View/Edit Analytics Pipeline', feature: 'analytics' },
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },
    {
        name: 'workflow-designer:visualize',
        path: '/analytics-pipelines/:id/visualisation',
        component: () =>
            import(/* webpackChunkName: "workflows" */ '@/modules/workflow-designer/views/WorkflowVisualisation.vue'),
        props: true,
        meta: { title: 'View visualisation', feature: 'analytics' },
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },
    {
        name: 'history',
        path: '/analytics-pipelines/:workflowId/history',
        component: () =>
            import(
                /* webpackChunkName: "workflows" */ '@/modules/workflow-designer/components/execution-history/AnalyticsExecutionHistory.vue'
            ),
        props: true,
        meta: { feature: 'analytics' },
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },

    {
        name: 'workflow:create',
        path: '/analytics-pipelines/create',
        component: () =>
            import(/* webpackChunkName: "workflows" */ '@/modules/workflow-designer/views/ConfigureWorkflow.vue'),
        props: true,
        meta: { title: 'Create Data Analytics Pipeline', feature: 'analytics' },
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },
    {
        name: 'workflow:monitoring',
        path: '/analytics-pipelines/monitoring',
        component: () =>
            import(
                /* webpackChunkName: "workflows" */ '@/modules/workflow-designer/views/AnalyticsPipelineMonitoring.vue'
            ),
        meta: { title: 'Analytics Pipelines Monitoring', feature: 'analytics' },
        props: true,
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },
    {
        name: 'workflow',
        path: '/analytics-pipelines/:id/:mode?',
        component: () =>
            import(/* webpackChunkName: "workflows" */ '@/modules/workflow-designer/views/ConfigureWorkflow.vue'),
        props: true,
        meta: { title: 'Data Analytics Pipeline', feature: 'analytics' },
        beforeEnter: multiguard([AuthGuard, OrganisationGuard, AdminOnlyGuard]),
    },
];
